import _ from "lodash";
import { getQuestionCaption } from "../customSections/career.js";
import { getCareerAvailableYears } from "../lib/cvDataUtilities.js";
import {
  formatDate,
  getDate,
  getItemsWithinDateRange,
  getYears,
  zeroPad
} from "../lib/dateHelpers.js";
import { getEffortText } from "../lib/formatUtilities.js";
import { escapeHtml, getProp } from "../lib/frontendUtilities.js";
import { breakPath } from "../lib/hierarchyUtilities.js";
import { getCitName, getPMdata } from "../lib/pubMed.js";
import { getLastPosition } from "../lib/reportUtilities.js";
import { capFirst, getField, getKey, keys } from "../lib/utilities.js";
import { CCManage } from "./cclib/CCManage.ts";

var sortBy = "mentor";
var mentees = [];

var generateAnnualReviewReport = function ({
  contextConstants,
  contextState,
  conference
}) {
  let { user, cvOwner, hierarchy, hierarchyNames } = contextConstants;
  let { sectionState, section, format, newDef, pmidList } = contextState;

  var oReq = new XMLHttpRequest();
  oReq.open(
    "GET",
    "/templateFiles/reports/AnnualReviewTemplate.docx?i=1",
    true
  );
  oReq.responseType = "arraybuffer";

  var overviewState = getKey(sectionState, section, {});

  var yearInfo = getCareerAvailableYears(newDef, conference, user, cvOwner);
  var availableYears = yearInfo.availableYears;
  var baseYear = yearInfo.baseYear;
  if (
    overviewState.yearIndex == null ||
    availableYears[overviewState.yearIndex] == null
  )
    overviewState.yearIndex = availableYears.findIndex(function (n) {
      return n == baseYear;
    });
  var viewYear = availableYears[overviewState.yearIndex];

  var cutoff = conference.cutoffDate || "1/1"; // Before this = last year. New section starts on this date
  var month = cutoff.split("/")[0] || "1";
  var date = cutoff.split("/")[1] || "1";

  var startDate = new Date(month + "/" + date + "/" + viewYear).getTime();
  var endDate =
    new Date(month + "/" + date + "/" + (viewYear + 1)).getTime() -
    1000 * 60 * 60 * 24;

  oReq.onload = function (oEvent) {
    var arrayBuffer = oReq.response;

    // if you want to access the bytes:
    var byteArray = new Uint8Array(arrayBuffer);

    // Load the docx file as a binary
    var content = byteArray; //fs.readFileSync(path.resolve(__dirname, 'template.docx'), 'binary');

    var zip = new PizZip(content);
    var doc = new Docxtemplater();
    doc.loadZip(zip);
    doc.setOptions({ linebreaks: true });

    // set the templateVariables
    var now = new Date();

    var data = {};

    data.genDate = dateFormat(now, "mmmm d, yyyy");

    var pathObj = breakPath(cvOwner.paths[0] || "", hierarchy, hierarchyNames);

    data.hospital =
      (pathObj.hospital || "") +
      (pathObj.dept != null ? " - DEPARTMENT OF " + pathObj.dept : "");
    data.dept2 = pathObj.dept != null ? pathObj.dept : "";

    var dateString =
      new Date().getFullYear() +
      "-" +
      zeroPad(new Date().getMonth() + 1, 2) +
      "-" +
      zeroPad(new Date().getDate(), 2);

    data.name = cvOwner.name;
    data.reviewPeriod =
      formatDate(new Date(startDate)) + " - " + formatDate(new Date(endDate));

    // See if last rank is valid, see if starting in period
    var currentApt = getItemsWithinDateRange(
      [getLastPosition(newDef, { format })].filter(function (n) {
        return n != null;
      }),
      startDate,
      endDate
    )[0];
    if (currentApt != null) {
      data.rank = currentApt.title;

      var itemStart = currentApt.time.start;
      var itemStartDate = new Date(
        (itemStart.month != null ? itemStart.month : "1") +
          "/1/" +
          itemStart.year
      ).getTime();
      if (itemStartDate >= startDate) {
        data.newRank = currentApt.title;
        data.promo = getDate(
          { time: { start: currentApt.time.start } },
          "limitedMonthAndFullYear"
        );
      }
    }

    data.gotPromo = data.promo != null ? "Yes" : "No";

    var minInclude = function (list, def) {
      if (list.length == 0) list.push(def);
      return list;
    };

    data.award = minInclude(
      getItemsWithinDateRange(
        getField(newDef, "honors"),
        startDate,
        endDate
      ).map(function (n) {
        return {
          1: n.title,
          2: getDate(n, "limitedMonthAndFullYear")
        };
      }),
      { 1: "", 2: "" }
    );

    data.grant = minInclude(
      getItemsWithinDateRange(
        getField(newDef, "projects"),
        startDate,
        endDate,
        { ignoreEnd: true }
      ).map(function (n) {
        return {
          1: n.type == "unfunded" ? "Unfunded" : n.fundingSource || "",
          2: n.title || "",
          3: n.type == "unfunded" ? "Unfunded" : n.directCosts || "",
          4: n.role || "",
          5: getDate(
            { time: { start: n.time.start } },
            "limitedMonthAndFullYear"
          )
        };
      }),
      { 1: "", 2: "", 3: "", 4: "", 5: "" }
    );
    // data.grant = [{'1': 'Test', '2': 'Test', '3': 'Test', '4': 'Test', '5': 'Test'}];

    data.pres = minInclude(
      getItemsWithinDateRange(
        getField(newDef, "presentations").filter(function (n) {
          return !n.invitedProfessorship;
        }),
        startDate,
        endDate
      ).map(function (n) {
        return {
          time: n.time,
          1: n.title,
          2: n.location,
          3: n.presType || "",
          4: capFirst(n.type || ""),
          5: n.wasPresent || "", // ? 'Y' : 'N',
          6: n.firstOrLastAuthor || "", // ? 'Y' : 'N',
          7: getDate(n, "limitedMonthAndFullYear")
        };
      }),
      { 1: "", 2: "", 3: "", 4: "", 5: "", 6: "" }
    );

    data.abs = minInclude(
      getItemsWithinDateRange(
        getField(newDef, "scholarship").filter(function (n) {
          return (
            n.type ==
            "Abstracts, Poster Presentations and Exhibits Presented at Professional Meetings"
          );
        }),
        startDate,
        endDate
      ).map(function (n) {
        return {
          time: n.time,
          1: n.line,
          2: n.wasPresent || "",
          3: n.firstOrLastAuthor || "",
          4: getDate(n, "limitedMonthAndFullYear")
        };
      }),
      { 1: "", 2: "", 3: "", 4: "" }
    );

    data.lec = minInclude(
      getItemsWithinDateRange(
        getField(newDef, "presentations").filter(function (n) {
          return !!n.invitedProfessorship;
        }),
        startDate,
        endDate
      ).map(function (n) {
        return {
          1: n.title,
          2: n.location,
          3: getDate(n, "limitedMonthAndFullYear")
        };
      }),
      { 1: "", 2: "", 3: "" }
    );

    var pubList = getItemsWithinDateRange(
      getField(newDef, "scholarship").filter(function (n) {
        return (
          [
            "Research Investigations",
            "Other peer-reviewed scholarship",
            "Scholarship without named authorship",
            "Reviews, chapters, monographs and editorials",
            "Case reports",
            "Books/textbooks for the medical or scientific community",
            "Letters to the Editor"
          ].indexOf(n.type) >= 0
        );
      }),
      startDate,
      endDate
    );

    var pmids = {};
    pubList.map(function (n) {
      if (n.PMID != null) pmids[n.PMID] = true;
    });

    getKey(newDef, "citationName", "");
    var citationNames = newDef["citationName"].split(";");

    getPMdata(
      keys(pmids),
      function (pmData) {
        data.pub = minInclude(
          pubList.map(function (n) {
            var item = {
              3: n.line,
              4: getDate(n, "limitedMonthAndFullYear")
            };

            item["1"] =
              n.type == "Research Investigations"
                ? "Journal Article/Review"
                : n.type;

            var simplify = function (text) {
              return text.toLowerCase().split(",").join("");
            };
            if (n.PMID != null && pmData[n.PMID] != null) {
              var authors = pmData[n.PMID].fetchData.authorList || [];

              var firstAuthors = authors
                .filter(function (author, i) {
                  return i == 0 || getCitName(author).indexOf("*") >= 0;
                })
                .map(function (author) {
                  return simplify(getCitName(author));
                });

              var firstAuthor = simplify(getCitName(authors[0]));
              var lastAuthor = simplify(getCitName(authors.slice(-1)[0]));

              var isFirstAuthor = citationNames.some(function (name) {
                return firstAuthors.some(function (author) {
                  return author.indexOf(simplify(name)) >= 0;
                });
              });
              var isLastAuthor = citationNames.some(function (name) {
                return lastAuthor.indexOf(simplify(name)) >= 0;
              });
              var isMiddleAuthor = citationNames.some(function (name) {
                return authors.slice(1, -1).some(function (author) {
                  return (
                    simplify(getCitName(author)).indexOf(simplify(name)) >= 0
                  );
                });
              });
              item["2"] = isFirstAuthor
                ? "First Author"
                : isLastAuthor
                  ? "Last Author"
                  : isMiddleAuthor
                    ? "Middle Author"
                    : "";
              // item['2'] = (firstAuthor || lastAuthor) ? 'First/Last Author' : 'Middle Author';
            } else {
              (n.line || "").split(". ").map(function (line) {
                // var authors = line.split('*').join('').trim().split(', ');
                var authors = line.split(", ");

                // *Shah AS, *Raghuram A, Kaur K, Lipson S, Shoshany T, Stevens R, O'Brien M, Howell D, Fleischman K, Barnack D, Molind H, Kuemmerle KH, and Brodsky JR. Specialty-Specific Diagnoses in Pediatric Patients With Postconcussion Syndrome: Experience From a Multidisciplinary Concussion Clinic. Clin J Sport Med. 2021 Feb 16. PMID: 33605602.

                var firstAuthors = authors
                  .filter(function (author, i) {
                    return i == 0 || author.indexOf("*") >= 0;
                  })
                  .map(function (author) {
                    return simplify(author);
                  });

                var firstAuthor = simplify(authors[0]);
                var lastAuthor = simplify(authors.slice(-1)[0]);

                var isFirstAuthor = citationNames.some(function (name) {
                  return (
                    firstAuthors.some(function (author) {
                      return author.indexOf(simplify(name)) >= 0;
                    }) ||
                    (simplify(line).split(simplify(name)).length > 0 &&
                      simplify(line).split(simplify(name))[0].trim().length ==
                        0)
                  );
                });
                var isLastAuthor = citationNames.some(function (name) {
                  return (
                    lastAuthor.indexOf(simplify(name)) >= 0 ||
                    (simplify(line).split(simplify(name)).length > 0 &&
                      simplify(line).split(simplify(name)).slice(-1)[0].trim()
                        .length == 0)
                  );
                });
                var isMiddleAuthor = citationNames.some(function (name) {
                  return authors.slice(1, -1).some(function (author) {
                    return simplify(author).indexOf(simplify(name)) >= 0;
                  });
                });

                if (isFirstAuthor || isLastAuthor || isMiddleAuthor) {
                  item["2"] = isFirstAuthor
                    ? "First Author"
                    : isLastAuthor
                      ? "Last Author"
                      : isMiddleAuthor
                        ? "Middle Author"
                        : "";
                }
              });
              if (item["2"] == null) item["2"] = ""; // 'Middle Author';
            }

            return item;
          }),
          { 3: "", 4: "" }
        );

        data.patent = minInclude(
          getItemsWithinDateRange(
            getField(newDef, "scholarship").filter(function (n) {
              return n.type == "Patents";
            }),
            startDate,
            endDate
          ).map(function (n) {
            return {
              1: n.line,
              2: getDate(n, "limitedMonthAndFullYear")
            };
          }),
          { 1: "", 2: "" }
        );

        var leadership = [];
        var extractLeadership = function (item, key, superKey) {
          getItemsWithinDateRange(item.roles || [], startDate, endDate).map(
            function (role) {
              var text = (role.role || "").toLowerCase().trim();
              if (text.indexOf("chair") >= 0) {
                leadership.push({
                  1:
                    (role.role == null ? "" : role.role + ", ") +
                    item[key] +
                    (item[superKey] != null ? ", " + item[superKey] : ""),
                  2: "Chairperson"
                });
              } else if (text.indexOf("committee") >= 0) {
                leadership.push({
                  1:
                    (role.role == null ? "" : role.role + ", ") +
                    item[key] +
                    (item[superKey] != null ? ", " + item[superKey] : ""),
                  2: "Committee Member"
                });
              } else if (
                text.indexOf("director") >= 0 ||
                text.indexOf("editor") >= 0
              ) {
                leadership.push({
                  1:
                    (role.role == null ? "" : role.role + ", ") +
                    item[key] +
                    (item[superKey] != null ? ", " + item[superKey] : ""),
                  2: "Director/Site PI, Editor"
                });
              }
            }
          );
        };

        getField(newDef, "committeeService").map(function (item) {
          extractLeadership(item, "title", "location");
        });

        getField(newDef, "societies").map(function (item) {
          extractLeadership(item, "society");
        });

        getItemsWithinDateRange(
          getField(newDef, "editorialActivities"),
          startDate,
          endDate
        ).map(function (item) {
          if (item.type == "Other Editorial Roles") {
            leadership.push({
              1: (item.role == null ? "" : item.role + ", ") + item.journal,
              2: "Director/Site PI, Editor"
            });
          }
        });

        getItemsWithinDateRange(
          getField(newDef, "administrativePositions"),
          startDate,
          endDate
        ).map(function (item) {
          var text = (item.title || "").toLowerCase().trim();
          if (
            text.indexOf("director") >= 0 ||
            text.indexOf("editor") >= 0 ||
            text.indexOf("pi") >= 0
          ) {
            leadership.push({
              1:
                item.title +
                (item.location != null ? " - " + item.location : ""),
              2: "Director/Site PI, Editor"
            });
          }
        });

        data.lead = minInclude(leadership, { 1: "", 2: "" });

        var teachingRows = getItemsWithinDateRange(
          getField(newDef, "teaching").filter(function (n) {
            return (
              [
                "Teaching of Students in Courses",
                "Formal Teaching of Residents, Clinical Fellows and Research Fellows (post-docs)"
              ].indexOf(n.type) >= 0
            );
          }),
          startDate,
          endDate
        );

        var isHarvard = function (text) {
          return [
            "MGH",
            "BWH",
            "Brigham",
            "BIDMC",
            "Beth Israel",
            "Harvard",
            "Massachusetts General",
            "Boston Children",
            "BCH",
            "Dana Farber",
            "DFCI",
            "SERI"
          ].some(function (key) {
            return text.toLowerCase().indexOf(key.toLowerCase()) >= 0;
          });
        };
        data.hCourse = minInclude(
          teachingRows
            .filter(function (n) {
              return isHarvard(n.location || "");
            })
            .map(function (n) {
              return {
                1: n.title,
                2: getEffortText(n)
              };
            }),
          { 1: "", 2: "" }
        );

        data.nhCourse = minInclude(
          teachingRows
            .filter(function (n) {
              return !isHarvard(n.location || "");
            })
            .map(function (n) {
              return {
                1: n.title,
                2: getEffortText(n)
              };
            }),
          { 1: "", 2: "" }
        );

        var teachingRows = getItemsWithinDateRange(
          getField(newDef, "teaching"),
          startDate,
          endDate
        );
        var mentorRows = teachingRows
          .filter(function (n) {
            return n.type == "Formally Mentored Trainees";
          })
          .map(function (n) {
            return {
              1: n.name,
              2: getEffortText(n), //.effort || '',
              3: "Formally Mentored Harvard Students (Medical, Dental, Graduate, and Undergraduate)"
            };
          })
          .concat(
            teachingRows
              .filter(function (n) {
                return n.type == "Other Mentored Trainees and Faculty";
              })
              .map(function (n) {
                return {
                  1: n.name,
                  2: getEffortText(n), // n.effort || '',
                  3: "Other Mentored Trainees and Faculty"
                };
              })
          );

        data.ment = minInclude(mentorRows, { 1: "", 2: "", 3: "" });

        var yearData = getKey(getKey(newDef, "career", {}), viewYear, {
          timeBreakdown: {},
          goals: {},
          status: "mentee",
          questions: {},
          mentorComments: ""
        });
        var lastYearData = newDef.career[viewYear - 1] || {
          timeBreakdown: {},
          goals: {},
          status: "mentee",
          questions: {},
          mentorComments: ""
        };

        var goalKeys = keys(conference.goals);

        data["lg"] = goalKeys.map(function (gKey, i) {
          var lData = lastYearData.goals[gKey] || {};
          var wasMet = !!lData.achieved;
          return {
            1: gKey,
            2: lData.text || "",
            3: wasMet ? "Y" : "N",
            4: wasMet ? "" : lData.barrier || ""
          };
        });

        data["ng"] = goalKeys.map(function (gKey, i) {
          var ndata = yearData.goals[gKey] || {};
          return {
            1: gKey,
            2: ndata.text || ""
          };
        });

        var timeKeys = [
          "research",
          "teaching",
          "clinical",
          "admin",
          "leadership"
        ];
        data["aa"] = timeKeys.map(function (type) {
          return {
            1: capFirst(type),
            2:
              (yearData.timeBreakdown[type] != null
                ? yearData.timeBreakdown[type]
                : 0) + "%"
          };
        });

        data["qs"] = keys(yearData.questions).map(function (qKey) {
          let localConferences = {};
          localConferences[conference.id] = conference;
          let questionCaption = getQuestionCaption(
            localConferences,
            newDef,
            qKey
          );
          return {
            1: questionCaption,
            2: (yearData.questions[qKey] || {}).text || ""
          };
        });

        console.log("Annual Review Data", data);

        doc.setData(data);

        try {
          doc.render();
        } catch (error) {
          console.log({
            message: error.message,
            name: error.name,
            stack: error.stack,
            properties: error.properties
          });
          throw error;
        }

        var fileName =
          "AnnualReview_" +
          cvOwner.name
            .split(",")
            .join("")
            .split(".")
            .join("")
            .split(" ")
            .join("_") +
          "_" +
          dateString +
          ".docx";
        saveAs(
          doc.getZip().generate({
            type: "blob",
            mimeType:
              "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          }),
          fileName.split("\n").join("")
        );
      },
      { pmidList: pmidList }
    );
  };
  oReq.send();
};

export default {
  title: "Harvard",
  defaults: {
    questions: ["cc_q1", "cc_q2", "cc_q3"],
    questionCaptions: [
      "Please list professional issues important for your academic development (i.e. need for departmental resources, faculty mentoring, or other support) and issues that are important for the function of the department/division.",
      "Comment on the performance of key support personnel/functions (if relevant to your position) and any constructive criticism that you might wish to add.",
      "Comment on other items of note that you wish to add."
    ]
  },
  cvSetup: function ({
    contextMethods,
    contextConstants,
    contextState,
    conference,
    backend
  }) {
    let { updateLayout, checkSave } = contextMethods;
    let { user, cvOwner, mentors, conferences } = contextConstants;
    let {
      section,
      sectionState,
      format,
      newDef,
      viewAsState,
      customExportSettings
    } = contextState;

    var editAccessOverride =
      (user.settings.accessFilters || []).length > 0 &&
      user.settings.conferenceTabAccess == "editAccess";
    var menteeOverride = false;
    var mentorOverride = false;

    var menteeAccess =
      user.id == cvOwner.id || editAccessOverride || menteeOverride;
    var overviewState = getKey(sectionState, section, {});

    var mentorAccess =
      (conference.mentors[user.id] || {})[cvOwner.id] != null ||
      editAccessOverride ||
      mentorOverride;
    var managerAccess = editAccessOverride;

    $("#fieldList").empty();

    $("#fieldList").append(
      $(
        "" +
          "<div>" +
          '   <div class="btn btn-default careerYearPrev" style="margin-right: 15px; margin-bottom: 8px;">' +
          '   <span class="glyphicon glyphicon-arrow-left"></span>' +
          "   </div>" +
          '   <h4 class="careerYear" style="margin-right: 15px;display: inline-block;margin-top: 7px;vertical-align: top;">Year: </h4>' +
          '   <div class="btn btn-default careerYearNext" style="margin-right: 15px;vertical-align: top;"><span class="glyphicon glyphicon-arrow-right"></span></div>' +
          '   <div class="btn btn-primary careerReportDownload" style="position: relative; margin-right: 5px; vertical-align: top; display: inline-block;">' +
          '     <span class="glyphicon glyphicon-download" style="margin-right: 5px;"></span>' +
          "     <span>Annual Career Report</span>" +
          "   </div>" +
          '   <div class="btn btn-primary limitedCVCareerDownload" style="position: relative; margin-right: 5px; vertical-align: top; display: inline-block;">' +
          '     <span class="glyphicon glyphicon-download" style="margin-right: 5px;"></span>' +
          "     <span>Date Limited CV</span>" +
          "   </div>" +
          '   <div class="btn btn-primary fullDownload" style="position: relative; margin-right: 5px; vertical-align: top; display: inline-block;">' +
          '     <span class="glyphicon glyphicon-download" style="margin-right: 5px;"></span>' +
          "     <span>Full CV</span>" +
          "   </div>" +
          '   <div class="btn btn-primary careerReportPrint" style="position: relative; margin-right: 5px; vertical-align: top; display: inline-block;">' +
          '     <span class="glyphicon glyphicon-print" style="margin-right: 5px;"></span>' +
          "     <span>Print</span>" +
          "   </div>" +
          " </div>"
      )
    );

    $(".careerReportDownload")
      .unbind("click")
      .click(function () {
        generateAnnualReviewReport({
          contextState,
          contextConstants,
          conference
        });
      });

    $(".careerReportPrint")
      .unbind("click")
      .click(function () {
        updateHTML({ readOnly: true });
        var printContents = $("#printableContents")[0].innerHTML;
        var w = window.open("", "Test");
        if (w == null) return;
        w.document.write(
          '<link rel="stylesheet" href="/css/bootstrap.min.css"/>'
        );
        w.document.write(
          "<style> @media print {  @page { margin: 1in; } } </style>"
        );
        var yearRange = $(".careerYear").text();
        w.document.write(
          '<div style="padding: 15px;"><h4 style="">' +
            yearRange +
            " " +
            (cvOwner.name || "") +
            " Career Conference</h4>" +
            printContents +
            "</div>"
        );
        updateHTML();
        w.document.title =
          yearRange + " " + (cvOwner.name || "") + " Career Conference";
        setTimeout(function () {
          w.print();
          // w.close();
        }, 1000);
      });

    $(".limitedCVCareerDownload")
      .unbind("click")
      .click(function () {
        var overviewState = getKey(sectionState, section, {});

        var yearInfo = getCareerAvailableYears(
          newDef,
          conference,
          user,
          cvOwner
        );
        var availableYears = yearInfo.availableYears;
        var baseYear = yearInfo.baseYear;
        if (
          overviewState.yearIndex == null ||
          availableYears[overviewState.yearIndex] == null
        )
          overviewState.yearIndex = availableYears.findIndex(function (n) {
            return n == baseYear;
          });
        var viewYear = availableYears[overviewState.yearIndex];

        var cutoff = conference.cutoffDate || "1/1"; // Before this = last year. New section starts on this date
        var month = cutoff.split("/")[0] || "1";
        var date = cutoff.split("/")[1] || "1";

        customExportSettings.dateLimited = true;
        customExportSettings.shown = true;

        var endDate = new Date(
          viewYear + 1,
          parseInt(month) - 1,
          parseInt(date) - 1
        );
        $("#m1").val(month);
        $("#m2").val(endDate.getMonth() + 1);
        $("#d1").val(date);
        $("#d2").val(endDate.getDate());
        $("#y1").val(viewYear + (format == "stanford_anesthesia" ? -1 : 0));
        $("#y2").val(endDate.getYear() + 1900);
        $(".download").trigger("click");
      });

    var mentorList = keys(conference.mentors).filter(function (id) {
      return conference.mentors[id][cvOwner.id] != null && mentors[id] != null;
    });

    if (mentorList.length > 0) {
      $("#fieldList").append(
        $(
          "" +
            '<div id="confSelectSection" style="margin-bottom: 10px;display: inline-block;width: 100%;">' +
            '  <h4 style="display: inline-block;margin-right: 5px;">Career Conference Mentor' +
            (mentorList.length > 1 ? "s" : "") +
            ": " +
            mentorList
              .map(function (mId) {
                return escapeHtml(mentors[mId].name);
              })
              .join(", ") +
            "</h4>" +
            "</div>"
        )
      );
    }

    $("#fieldList").append(
      $(
        "" +
          '<div style="margin-top: 8px;display: inline-block;width: 100%;">' +
          '  <h4 class="statusTitle" style="display: inline-block; margin-right: 5px;">Status: Mentee Editing</h4>' +
          '  <div class="btn prevStatus btn-default" style="vertical-align: top;margin-top: 3px;"></div>' +
          '  <h5 class="errorSubheader" style="color: #ad310e;">Missing Information Required Before Submission</h5>' +
          '  <h5 class="statusSubheader">When finished, mark as complete to notify your mentor.</h5>' +
          "</div>"
      )
    );

    var last = getLastPosition(newDef, { format });
    if (
      last != null &&
      last.time != null &&
      last.time.start != null &&
      !isNaN(getYears(last.time.start))
    ) {
      var dateText = getDate(last);
      var yearsLong = Math.round(last.years * 10) / 10; // Math.round(((last.time.end != null && last.time.end != 'ongoing') ? getYears(last.time.end) : (new Date().getYear()+1900)) - getYears(last.time.start));
      $("#fieldList").append(
        $(
          "" +
            '<div style="margin-bottom: 10px;margin-top: 10px;">' +
            '  <h4 style="margin-bottom: 10px; display: inline-block;">Academic Title: <span id="careerConfCurrentTitle"><u class="sectionTab" data-section="facultyAppointments">' +
            escapeHtml(last.title) +
            " (" +
            escapeHtml(dateText) +
            ")</u> for " +
            yearsLong +
            " year" +
            (yearsLong != 1 ? "s" : "") +
            "</span></h4>" +
            "  <h4>Rank: " +
            ({
              fellow: "Fellow",
              assistant: "Assistant Professor",
              associate: "Associate Professor",
              full: "Full Professor"
            }[last.role] || "Unknown") +
            "</h4>" +
            "<div>"
        )
      );
    }

    var printableContents = $('<span id="printableContents"></span>');
    $("#fieldList").append(printableContents);

    // Find which years are available. Should be just years which have data or which are open, or if you have mentorAccess
    var yearInfo = getCareerAvailableYears(newDef, conference, user, cvOwner);
    var availableYears = yearInfo.availableYears;
    var baseYear = yearInfo.baseYear;

    var overviewState = getKey(sectionState, section, {});
    if (
      overviewState.yearIndex == null ||
      availableYears[overviewState.yearIndex] == null
    )
      overviewState.yearIndex = availableYears.findIndex(function (n) {
        return n == baseYear;
      });

    var timeKeys = [
      { name: "Research", key: "research" },
      { name: "Teaching/Training", key: "teaching" },
      { name: "Clinical", key: "clinical" },
      { name: "Administrative", key: "admin" },
      {
        name: "Leadership Roles (i.e. Committee Chair, Program Director, etc)",
        key: "leadership"
      }
    ];

    var goalKeys = keys(conference.goals);

    printableContents.append(
      $(
        "" +
          '<div style="margin-bottom: 30px;margin-top: 30px;">' +
          "  <h4>Academic Activity Breakdown</h4>" +
          timeKeys
            .map(function (item, i) {
              return (
                "" +
                '<div style="display: block; margin-bottom: 5px;">' +
                '<span class="careerTimeBreakdownInputText" style="display: inline-block; width: 80px; margin-right: 0px; text-align: center;" data-type="' +
                item.key +
                '"></span>' +
                '<input class="form-control careerTimeBreakdownInput" data-type="' +
                item.key +
                '" style="display: inline-block;width: 80px;margin-right: 5px; text-align: center">' +
                "<span>% - <b>" +
                escapeHtml(item.name) +
                "</b></span>" +
                "</div>"
              );
            })
            .join("") +
          '<div style="display: block; margin-bottom: 5px;">' +
          '  <span class="careerTimeTotal" style="display: inline-block;width: 80px;margin-right: 0px; text-align: center">100</span>' +
          ' <span>% <b style="margin-left: 8px;">Total</b></span>' +
          "</div>" +
          "<div>"
      )
    );

    printableContents.append(
      $(
        "" +
          '<div style="margin-bottom: 20px;">' +
          '  <h4 style="margin-bottom: 20px;">Professional Goals</h4>' +
          "</div>"
      )
    );

    goalKeys.map(function (item) {
      printableContents.append(
        $(
          "" +
            '<div class="careerGoalSection" data-type="' +
            escapeHtml(item) +
            '" style="display: block; margin-bottom: 5px;">' +
            '  <h5 style="font-size: 17px;margin-bottom: 15px;">' +
            escapeHtml(item) +
            "</h5>" +
            '  <div style="margin-left: 20px;">' +
            '    <div class="lastYearGoalSection">' +
            "      <h5>Last Year's Goal</h5>" +
            '      <span class="lastYearGoalText"></span>' +
            '      <h5>Goal achieved? <input class="lastYearGoalAchieved" type="checkbox" style="margin-right: 10px;margin-left: 5px;margin-top: 1px;vertical-align: top;"></h5>' +
            '      <h5><span class="lastYearGoalTitle">What were your barriers for not achieving this goal?</span></h5>' +
            "      <div>" +
            '        <div class="lastYearGoalBarrierText"></div>' +
            '        <textarea class="form-control lastYearGoalBarrier" style="resize: vertical; min-height: 60px;" placeholder=""></textarea>' +
            "      </div>" +
            "    </div>" +
            "    <div>" +
            "      <h5>Goal for Upcoming Year</h5>" +
            '      <div class="thisYearGoalText"></div>' +
            '      <textarea class="form-control thisYearGoal" style="resize: vertical; min-height: 60px;" placeholder=""></textarea>' +
            "    </div>" +
            '    <div class="careerGoalMentorSection">' +
            "      <h5>Mentor Comments</h5>" +
            '      <div class="goalMentorCommentsText"></div>' +
            '      <textarea class="form-control goalMentorComments" style="resize: vertical; min-height: 60px;" placeholder=""></textarea>' +
            "    </div>" +
            "  </div>" +
            "</div>"
        )
      );
    });

    printableContents.append(
      $(
        "" +
          '<div style="margin-top: 20px;">' +
          '  <h4 style="margin-bottom: 20px;">Other Questions</h4>' +
          '  <div id="careerQuestionSection"></div>' +
          "</div>" +
          '<div class="generalMentorCommentSection" style="margin-top: 20px;">' +
          '  <h4 style="margin-bottom: 15px;">General Mentor Comments</h4>' +
          '  <div class="generalMentorCommentsText"></div>' +
          '  <textarea class="form-control generalMentorComments" style="resize: vertical; min-height: 60px;"></textarea>' +
          "</div>"
      )
    );

    $("#fieldList").append(
      $(
        "" +
          '<div style="margin-top: 8px;display: inline-block;width: 100%;">' +
          '  <h4 class="statusTitle" style="display: inline-block; margin-right: 5px;">Status: Mentee Editing</h4>' +
          '  <div class="btn nextStatus btn-success disabled" style="vertical-align: top; margin-top: 3px; display: inline-block;">Submit for Review</div>' +
          '  <div class="btn prevStatus btn-default" style="vertical-align: top; margin-top: 3px; display: none;"></div>' +
          '  <h5 class="errorSubheader" style="color: #ad310e;"></h5>' +
          '  <h5 class="statusSubheader" style="display: none;">When finished, submit for review to notify your mentor</h5>' +
          "</div>"
      )
    );

    var careerErrorCheck = function () {
      var overviewState = getKey(sectionState, section, {});
      // if (overviewState.yearIndex == null) overviewState.yearIndex = 0;
      if (
        overviewState.yearIndex == null ||
        availableYears[overviewState.yearIndex] == null
      )
        overviewState.yearIndex = availableYears.findIndex(function (n) {
          return n == baseYear;
        });
      var viewYear = availableYears[overviewState.yearIndex];

      var yearData = getKey(newDef.career, viewYear, {
        timeBreakdown: {},
        goals: {},
        status: "mentee",
        questions: {},
        mentorComments: ""
      });

      var lastYearData = newDef.career[viewYear - 1] || {
        timeBreakdown: {},
        goals: {},
        status: "mentee",
        questions: {},
        mentorComments: ""
      };

      var confYearData = conference.openYears[viewYear] || { open: false };
      var isOpen = !!confYearData.open || managerAccess; // For managers, it's always open.

      var totalPercent = 0;
      (timeKeys || []).map(function (item) {
        if (!isNaN(yearData.timeBreakdown[item.key]))
          totalPercent += yearData.timeBreakdown[item.key];
      });
      if (isNaN(totalPercent)) totalPercent = 0;

      var questionKeys = _.uniq(
        (conference.questions || []).concat(keys(yearData.questions))
      );
      questionKeys = questionKeys.filter(function (question) {
        return (
          conference.questions.indexOf(question) >= 0 ||
          (yearData.questions[question] || { text: "" }).text.trim().length > 0
        );
      });

      var errors = [];
      var hasError = false;
      if (!isOpen) {
      } else if (yearData.status == "mentee") {
        if (totalPercent != null && totalPercent != 100) {
          errors.push("Academic activity must total 100%");
          hasError = true;
        }
        if (
          goalKeys.some(function (item) {
            var goalInfo = getKey(yearData.goals, item, {
              achieved: false,
              text: "",
              barrier: "",
              mentorComments: ""
            });
            var lastGoalInfo = getKey(lastYearData.goals, item, {
              achieved: false,
              text: "",
              barrier: "",
              mentorComments: ""
            });
            return (
              goalInfo.text.length == 0 ||
              (newDef.career[viewYear - 1] != null &&
                !lastGoalInfo.achieved &&
                (lastGoalInfo.text || "").length > 0 &&
                (lastGoalInfo.barrier || "").length == 0)
            );
          }) ||
          questionKeys.some(function (item, i) {
            var question = questionKeys[i];
            getKey(yearData.questions, question, {
              text: "",
              mentorComments: ""
            });
            return yearData.questions[question].text.length == 0;
          })
        ) {
          errors.push("Missing information required before submission");
          hasError = true;
        }
      } else if (yearData.status == "mentor") {
        hasError =
          goalKeys.some(function (item) {
            var goalInfo = getKey(yearData.goals, item, {
              achieved: false,
              text: "",
              barrier: "",
              mentorComments: ""
            });
            return (goalInfo.mentorComments || "").length == 0;
          }) || (yearData.mentorComments || "").length == 0;
      }

      if (!isOpen) {
        $(".statusSubheader").text("");
        $(".errorSubheader").text("");
      } else if (yearData.status == "mentee" && menteeAccess) {
        $(".statusSubheader").text(
          "When finished, submit for review to notify your mentor"
        );
        $(".errorSubheader").text(errors.join(", "));
      } else if (yearData.status == "mentor" && mentorAccess) {
        $(".statusSubheader").text("When finished, mark as finalized");
        $(".errorSubheader").text(
          "Missing information required before finalizing"
        );
      } else {
        $(".statusSubheader").text("");
        $(".errorSubheader").text("");
      }

      $(".statusSubheader").toggle(!hasError);
      $(".errorSubheader").toggle(hasError);

      $(".errorSubheader")
        .unbind("click")
        .click(function () {
          $(".has-error")[0].scrollIntoView();
        });
      $(".nextStatus").toggleClass("disabled", hasError);
    };

    var updateHTML = function (opts) {
      opts = opts || {};
      var overviewState = getKey(sectionState, section, {});
      // if (overviewState.yearIndex == null) overviewState.yearIndex = 0;
      if (
        overviewState.yearIndex == null ||
        availableYears[overviewState.yearIndex] == null
      )
        overviewState.yearIndex = availableYears.findIndex(function (n) {
          return n == baseYear;
        });
      var viewYear = availableYears[overviewState.yearIndex];

      var cutoff = conference.cutoffDate || "1/1"; // Before this = last year. New section starts on this date
      var month = cutoff.split("/")[0] || "1";
      var date = cutoff.split("/")[1] || "1";

      var startDate = new Date(month + "/" + date + "/" + viewYear).getTime();
      var endDate =
        new Date(month + "/" + date + "/" + (viewYear + 1)).getTime() -
        1000 * 60 * 60 * 24;

      var yearData = getKey(newDef.career, viewYear, {
        timeBreakdown: {},
        goals: {},
        status: "mentee",
        questions: {},
        mentorComments: "",
        details: {}
      });

      var lastYearData = newDef.career[viewYear - 1] || {
        timeBreakdown: {},
        goals: {},
        status: "mentee",
        questions: {},
        mentorComments: "",
        details: {}
      };

      getKey(yearData, "status", "mentee"); // mentee, mentor, finalized

      var confYearData = conference.openYears[viewYear] || { open: false };
      var isOpen = !!confYearData.open || managerAccess;

      var isReadOnly = function (yearData, type, thisOpts) {
        thisOpts = thisOpts || {};
        if (opts.readOnly) return true;
        var editAccessOverride =
          (user.settings.accessFilters || []).length > 0 &&
          user.settings.conferenceTabAccess == "editAccess";
        if (thisOpts.noOverride) editAccessOverride = false;
        var menteeOverride = false;
        var mentorOverride = false;

        if (viewAsState !== "undefined" && editAccessOverride) {
          editAccessOverride = false;
          if (viewAsState.career == "mentee") menteeOverride = true;
          else if (viewAsState.career == "mentor") mentorOverride = true;
          else editAccessOverride = true;
        }

        var menteeAccess =
          user.id == cvOwner.id || editAccessOverride || menteeOverride;
        var mentorAccess =
          (conference.mentors[user.id] || {})[cvOwner.id] != null ||
          editAccessOverride ||
          mentorOverride;
        return (
          !isOpen ||
          (yearData.status != type && !mentorAccess && !thisOpts.noOverride) ||
          (type == "mentee" && !menteeAccess) ||
          (type == "mentor" && !mentorAccess)
        );
      };

      $(".generalMentorCommentSection").toggle(
        isOpen &&
          (yearData.status != "mentee" ||
            mentorAccess ||
            (yearData.mentorComments || "").length > 0)
      );

      $(".statusTitle").text(
        "Status: " +
          (!isOpen
            ? "Closed"
            : {
                mentee: "Mentee Editing",
                mentor: "Mentor Review",
                finalized: "Finalized"
              }[yearData.status])
      );

      $(".nextStatus, .prevStatus").hide();
      if (!isOpen) {
      } else if (yearData.status == "mentee") {
        if (menteeAccess) $(".nextStatus").show().text("Submit for Review");
      } else if (yearData.status == "mentor") {
        if (menteeAccess && !!confYearData.open)
          $(".prevStatus").show().text("Resume Editing");
        if (mentorAccess) $(".nextStatus").show().text("Finalize Review");
      } else if (yearData.status == "finalized") {
        if (mentorAccess) $(".prevStatus").show().text("Resume Review");
      }

      var getYearText = function (obj) {
        return (
          obj.getMonth() +
          1 +
          "/" +
          obj.getDate() +
          "/" +
          (obj.getYear() + 1900 + "").slice(-2)
        );
      };
      if (isNaN(startDate)) debugger;

      $(".careerYear").html(
        "<span>" +
          getYearText(new Date(startDate)) +
          " - " +
          getYearText(new Date(endDate)) +
          "</span>" +
          (!isOpen || yearData.status == "closed"
            ? '<span class="glyphicon glyphicon-lock" style="margin-left: 10px;"></span>'
            : "")
      );

      $(".careerYearPrev")
        .css("visibility", overviewState.yearIndex > 0 ? "" : "hidden")
        .unbind("click")
        .click(function () {
          // Only go back if previous data exists
          overviewState.yearIndex--;
          updateHTML();
        });
      $(".careerYearNext")
        .toggle(overviewState.yearIndex < availableYears.length - 1)
        .unbind("click")
        .click(function () {
          overviewState.yearIndex++;
          updateHTML();
        });

      var totalPercent = 0;
      (timeKeys || []).map(function (item, i) {
        var timeBreakdownInput = $(
          '.careerTimeBreakdownInput[data-type="' + item.key + '"]'
        );
        timeBreakdownInput.unbind("input").on("input", function () {
          var key = getProp(this, "type");
          var num = parseInt($(this).val());
          if ($(this).val().trim().length == 0) num = 0;

          var yearData = newDef.career[viewYear];

          var amountErr = isNaN(num) || num < 0 || num > 100;
          $(this).parent().toggleClass("has-error", amountErr);
          if (amountErr) return;
          yearData.timeBreakdown[key] = num;

          var newTotal = 0;
          timeKeys.map(function (item) {
            if (!isNaN(yearData.timeBreakdown[item.key]))
              newTotal += yearData.timeBreakdown[item.key];
          });
          if (isNaN(newTotal)) return;
          $(".careerTimeTotal")
            .text(newTotal)
            .css("color", newTotal != 100 ? "red" : "black")
            .toggleClass("has-error", newTotal != 100);

          checkSave();
          careerErrorCheck();
        });

        var val = yearData.timeBreakdown[item.key];
        timeBreakdownInput
          .toggle(!isReadOnly(yearData, "mentee"))
          .val(val != null ? val : 0);
        $('.careerTimeBreakdownInputText[data-type="' + item.key + '"]')
          .toggle(isReadOnly(yearData, "mentee"))
          .text(val != null ? val : 0);

        if (!isNaN(yearData.timeBreakdown[item.key]))
          totalPercent += yearData.timeBreakdown[item.key];
      });

      if (isNaN(totalPercent)) totalPercent = 0;
      $(".careerTimeTotal")
        .text(totalPercent)
        .css("color", totalPercent != 100 ? "red" : "black")
        .toggleClass("has-error", totalPercent != 100);

      // keys(yearData.questions).map(function(question) {
      //   if ((yearData.questions[question].text || '').trim().length == 0 && (yearData.questions[question].mentorComments || '').trim().length == 0) delete yearData.questions[question];
      // });

      var questionKeys = _.uniq(
        (conference.questions || []).concat(keys(yearData.questions))
      );
      questionKeys = questionKeys.filter(function (question) {
        return (
          conference.questions.indexOf(question) >= 0 ||
          (yearData.questions[question] || { text: "" }).text.trim().length > 0
        );
      });

      careerErrorCheck();

      $(".nextStatus")
        .unbind("click")
        .click(function () {
          if ($(this).hasClass("disabled")) {
            $(".has-error")[0].scrollIntoView();
            return;
          }
          var yearData = newDef.career[viewYear];

          var logText = JSON.stringify({
            cvOwnerId: cvOwner.id,
            userId: user.id,
            data: newDef.career
          });
          backend.send("log", logText);
          // $.get('/log?text='+encodeURIComponent(logText));

          if (yearData.status == "mentee") {
            var check = confirm(
              "Are you sure you are ready to submit? This will send a notification to your mentor that you have completed this form."
            );
            if (!check) return;
            yearData.status = "mentor";
            getKey(yearData, "statusTimes", {})[yearData.status] = Date.now();

            backend.send("sendEmail", {
              type: "mentorAlert1",
              data: {
                mentorList: mentorList,
                cvOwnerId: cvOwner.id
              }
            });
          } else if (yearData.status == "mentor") {
            var check = confirm(
              "Are you sure you are ready to finalize? This will send a notification to your mentee that you have completed this review."
            );
            if (!check) return;
            yearData.status = "finalized";
            getKey(yearData, "statusTimes", {})[yearData.status] = Date.now();

            backend.send("sendEmail", {
              type: "menteeAlert1",
              data: {
                mentorId: user.id,
                cvOwnerId: cvOwner.id
              }
            });
          }
          checkSave();
          updateHTML();
        });

      $(".prevStatus")
        .unbind("click")
        .click(function () {
          if (yearData.status == "mentor") yearData.status = "mentee";
          else if (yearData.status == "finalized") yearData.status = "mentor";
          // getKey(yearData, 'statusTimes', {})[yearData.status] = Date.now();
          checkSave();
          updateHTML();
        });

      goalKeys.map(function (item) {
        getKey(yearData.goals, item, {
          achieved: false,
          text: "",
          barrier: "",
          mentorComments: ""
        });
        getKey(lastYearData.goals, item, {
          achieved: false,
          text: "",
          barrier: "",
          mentorComments: ""
        });

        var goalSection = $('.careerGoalSection[data-type="' + item + '"]');

        var lastYearExists = lastYearData.goals[item].text.length > 0;
        var lastYearNotAchieved = !lastYearData.goals[item].achieved;

        goalSection.find(".lastYearGoalSection").toggle(lastYearExists);
        goalSection
          .find(".lastYearGoalTitle, .lastYearGoalBarrier")
          .toggle(lastYearNotAchieved);
        goalSection
          .find(".lastYearGoalText")
          .html((lastYearData.goals[item].text || "").split("\n").join("<br>"));

        goalSection
          .find(".lastYearGoalBarrier")
          .toggle(!isReadOnly(yearData, "mentee"))
          .val(lastYearData.goals[item].barrier)
          .parent()
          .toggleClass(
            "has-error",
            lastYearNotAchieved &&
              lastYearExists &&
              (lastYearData.goals[item].barrier || "").length == 0
          );

        goalSection
          .find(".lastYearGoalBarrierText")
          .toggle(isReadOnly(yearData, "mentee"))
          .html(
            (lastYearData.goals[item].barrier || "").split("\n").join("<br>")
          );

        goalSection
          .find(".lastYearGoalBarrier")
          .unbind("input")
          .on("input", function () {
            var key = getProp(this, "type");
            var lastYearData = newDef.career[viewYear - 1] || {
              timeBreakdown: {},
              goals: {},
              status: "mentee",
              questions: {},
              mentorComments: ""
            };
            lastYearData.goals[key].barrier = $(this).val().trim();
            $(this)
              .parent()
              .toggleClass(
                "has-error",
                lastYearData.goals[key].barrier.length == 0
              );
            checkSave();
            careerErrorCheck();
          });

        goalSection
          .find(".lastYearGoalAchieved")
          .attr(
            "onclick",
            isReadOnly(yearData, "mentee") ? "return false;" : ""
          )
          .prop("checked", !lastYearNotAchieved);
        goalSection
          .find(".lastYearGoalAchieved")
          .unbind("change")
          .on("change", function () {
            var key = getProp(this, "type");
            var lastYearData = newDef.career[viewYear - 1] || {
              timeBreakdown: {},
              goals: {},
              status: "mentee",
              questions: {},
              mentorComments: ""
            };
            lastYearData.goals[key].achieved = $(this).is(":checked");
            checkSave();
            updateHTML();
          });

        goalSection
          .find(".thisYearGoal")
          .toggle(!isReadOnly(yearData, "mentee"))
          .html((yearData.goals[item].text || "").split("\n").join("<br>")) //.val(yearData.goals[item].text)
          .parent()
          .toggleClass(
            "has-error",
            isOpen && (yearData.goals[item].text || "").length == 0
          );
        goalSection
          .find(".thisYearGoalText")
          .toggle(isReadOnly(yearData, "mentee"))
          .html((yearData.goals[item].text || "").split("\n").join("<br>"));

        goalSection
          .find(".thisYearGoal")
          .unbind("input")
          .on("input", function () {
            var key = getProp(this, "type");
            var yearData = newDef.career[viewYear];
            yearData.goals[key].text = $(this).val().trim();

            var logText =
              "q - " +
              key +
              " - text - " +
              yearData.goals[key].text +
              " - user " +
              (user || {}).id;
            // $.get('/log?text='+encodeURIComponent(logText));
            backend.send("log", logText);

            $(this)
              .parent()
              .toggleClass("has-error", yearData.goals[key].text.length == 0);
            checkSave();
            careerErrorCheck();
          });

        goalSection
          .find(".goalMentorComments")
          .toggle(!isReadOnly(yearData, "mentor"))
          .val(yearData.goals[item].mentorComments || "")
          .parent()
          .toggleClass(
            "has-error",
            !isReadOnly(yearData, "mentor") &&
              (yearData.goals[item].mentorComments || "").length == 0
          )
          .toggle(
            (isOpen && (yearData.status != "mentee" || mentorAccess)) ||
              (yearData.goals[item].mentorComments || "").length > 0
          );

        goalSection
          .find(".goalMentorCommentsText")
          .toggle(isReadOnly(yearData, "mentor"))
          .html(
            (yearData.goals[item].mentorComments || "").split("\n").join("<br>")
          );

        goalSection
          .find(".goalMentorComments")
          .unbind("input")
          .on("input", function () {
            var key = getProp(this, "type");
            var yearData = newDef.career[viewYear];
            yearData.goals[key].mentorComments = $(this).val().trim();

            var logText =
              "q - " +
              key +
              " - mentor - " +
              yearData.goals[key].mentorComments +
              " - user " +
              (user || {}).id;
            // $.get('/log?text='+encodeURIComponent(logText));
            backend.send("log", logText);

            $(this)
              .parent()
              .toggleClass(
                "has-error",
                yearData.goals[key].mentorComments.length == 0
              );
            checkSave();
            careerErrorCheck();
          });
      });

      $("#careerQuestionSection").empty();
      questionKeys.map(function (item, i) {
        let caption = getQuestionCaption(conferences, newDef, item);

        $("#careerQuestionSection").append(
          $(
            "" +
              '<div class="careerQuestionRow" data-type="' +
              i +
              '" style="display: block; margin-bottom: 30px;">' +
              "  <div>" +
              '    <h5 style="margin-bottom: 10px;">' +
              escapeHtml(caption) +
              "</h5>" +
              '    <div class="careerQuestionInputText"></div>' +
              '    <textarea class="form-control careerQuestionInput"></textarea>' +
              "  </div>" +
              "  <div>" +
              '    <h5 style="margin-bottom: 10px;">Mentor Response (Optional)</h5>' +
              '    <div class="careerQuestionMentorInputText"></div>' +
              '    <textarea class="form-control careerQuestionMentorInput"></textarea>' +
              "  </div>" +
              "</div>"
          )
        );
      });

      questionKeys.map(function (item, i) {
        var question = questionKeys[i];
        if (
          yearData.questions[question] != null &&
          typeof yearData.questions[question] == "string"
        )
          delete yearData.questions[question];
        getKey(yearData.questions, question, { text: "", mentorComments: "" });

        var goalSection = $('.careerQuestionRow[data-type="' + i + '"]');
        goalSection
          .find(".careerQuestionInput")
          .toggle(!isReadOnly(yearData, "mentee"))
          .val(yearData.questions[item].text)
          .parent()
          .toggleClass("has-error", yearData.questions[item].text.length == 0);

        goalSection
          .find(".careerQuestionInputText")
          .toggle(isReadOnly(yearData, "mentee"))
          .html((yearData.questions[item].text || "").split("\n").join("<br>"));

        goalSection
          .find(".careerQuestionInput")
          .unbind("input")
          .on("input", function () {
            var i = getProp(this, "type");
            var question = questionKeys[i];

            var text = $(this).val().trim();

            var logText =
              "q - " +
              question +
              " - text - " +
              text +
              " - user " +
              (user || {}).id;
            // $.get('/log?text='+encodeURIComponent(logText));
            backend.send("log", logText);

            var yearData = newDef.career[viewYear];
            getKey(yearData.questions, question, {
              text: "",
              mentorComments: ""
            }).text = text;

            $(this)
              .parent()
              .toggleClass(
                "has-error",
                yearData.questions[question].text.length == 0
              );

            checkSave();
            careerErrorCheck();
          });

        goalSection
          .find(".careerQuestionMentorInput")
          .toggle(!isReadOnly(yearData, "mentor"))
          .val(yearData.questions[item].mentorComments || "")
          .parent()
          .toggleClass(
            "has-error",
            !isReadOnly(yearData, "mentor") &&
              (yearData.questions[question].mentorComments || "").length == 0
          )
          .toggle(
            (isOpen && (yearData.status == "mentor" || mentorAccess)) ||
              (yearData.questions[question].mentorComments || "").length > 0
          );

        goalSection
          .find(".careerQuestionMentorInputText")
          .toggle(isReadOnly(yearData, "mentor"))
          .html(
            (yearData.questions[item].mentorComments || "")
              .split("\n")
              .join("<br>")
          );

        goalSection
          .find(".careerQuestionMentorInput")
          .unbind("input")
          .on("input", function () {
            var i = getProp(this, "type");
            var question = questionKeys[i];
            var yearData = newDef.career[viewYear];
            getKey(yearData.questions, question, {
              text: "",
              mentorComments: ""
            }).mentorComments = $(this).val().trim();

            var logText =
              "q - " +
              question +
              " - mentor - " +
              $(this).val().trim() +
              " - user " +
              (user || {}).id;
            // $.get('/log?text='+encodeURIComponent(logText));
            backend.send("log", logText);

            $(this)
              .parent()
              .toggleClass(
                "has-error",
                yearData.questions[question].mentorComments.length == 0
              );

            checkSave();
            careerErrorCheck();
          });
      });

      $(".generalMentorComments")
        .toggle(!isReadOnly(yearData, "mentor"))
        .val(getKey(yearData, "mentorComments", ""))
        .parent()
        .toggleClass(
          "has-error",
          !isReadOnly(yearData, "mentor") &&
            (yearData.mentorComments || "").length == 0
        );

      $(".generalMentorCommentsText")
        .toggle(isReadOnly(yearData, "mentor"))
        .html(getKey(yearData, "mentorComments", "").split("\n").join("<br>"));

      $(".generalMentorComments")
        .unbind("input")
        .on("input", function () {
          var yearData = newDef.career[viewYear];
          yearData.mentorComments = $(this).val().trim();
          $(this)
            .parent()
            .toggleClass("has-error", yearData.mentorComments.length == 0);
          checkSave();
          careerErrorCheck();
        });

      updateLayout();
    };

    updateHTML();
  },
  manageSetup: function (
    settingsEl,
    { conference, updateConfDate, saveConference, hierarchyNames },
    user
  ) {
    const ccManage = new CCManage(conference, saveConference, updateConfDate, {
      includeSections: {
        cutoff: true,
        mentees: true,
        goals: true,
        questions: true,
        mentors: true,
        reminders: false,
        years: true
      },
      sortBy: "mentor",
      verbage: {
        mentor: "Mentor",
        mentee: "Mentee"
      }
    });
    ccManage.createSettingsElements(settingsEl);
    updateConfDate(conference);
    ccManage.createMentorsMenteesSection();
    ccManage.createAwesomplete();
    ccManage.hookMessageButton(user);
    ccManage.populateMenteesSection(hierarchyNames);
  }
};
